import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { checklistCatalog } from './checklistCatalog';
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const labels = checklistCatalog.flatMap((group) => group.labels);
export function ChecklistLibrary({
  base,
  existing,
  patientId,
  onTemplate,
}: {
  base: string;
  patientId: string;
  existing: string[];
  onTemplate: (labels: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const cache = useQueryClient();
  const existingLabels = new Set(existing.map((label) => label.toLowerCase()));
  const add = useMutation({
    onSuccess: () => {
      setSelected(new Set());
      void cache.invalidateQueries({ queryKey: ['patient-checklist'] });
    },
    mutationFn: async () =>
      (
        await clientApi.post<{ added: number }>(`${base}/patients/${patientId}/selection`, {
          labels: [...selected],
        })
      ).data,
  });
  const groups = checklistCatalog
    .map((group) => ({
      ...group,
      labels: group.labels.filter((label) => `${group.name} ${label}`.toLowerCase().includes(search.toLowerCase())),
    }))
    .filter((group) => group.labels.length);
  function toggle(label: string, checked: boolean) {
    setSelected((previous) => {
      const next = new Set(previous);
      if (checked) next.add(label);
      else next.delete(label);
      return next;
    });
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Checklist options</CardTitle>
        <CardDescription>
          Choose tasks by visit stage or topic. Selected tasks start open; you can customize them or save a template.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Field>
          <FieldLabel htmlFor="checklist-search">Search checklist options</FieldLabel>
          <Input id="checklist-search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Field>
        <div className="max-h-96 overflow-auto">
          {!groups.length && <p>No matching options.</p>}
          {groups.map((group) => (
            <details key={group.name} open={search ? true : undefined}>
              <summary>
                {group.name} ({group.labels.length})
              </summary>
              <FieldSet className="p-3">
                <FieldLegend className="sr-only">{group.name}</FieldLegend>
                <FieldGroup>
                  {group.labels.map((label) => (
                    <Field key={label} orientation="horizontal">
                      <Checkbox
                        id={`library-${labels.indexOf(label)}`}
                        disabled={add.isPending}
                        checked={selected.has(label)}
                        onCheckedChange={(checked) => toggle(label, checked === true)}
                      />
                      <FieldLabel htmlFor={`library-${labels.indexOf(label)}`}>
                        {label}
                        {existingLabels.has(label.toLowerCase()) && ' · already on checklist'}
                      </FieldLabel>
                    </Field>
                  ))}
                </FieldGroup>
              </FieldSet>
            </details>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          Resource titles are available as tasks. Links truncated in the reference screenshots need to be added to your
          template or task notes.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button disabled={!selected.size || add.isPending} onClick={() => add.mutate()}>
            Add selected tasks ({selected.size})
          </Button>
          <Button
            variant="outline"
            disabled={!selected.size || add.isPending}
            onClick={() => onTemplate([...selected])}>
            Use selection in a template
          </Button>
          <Button variant="ghost" disabled={!selected.size || add.isPending} onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </div>
        {add.data && <p role="status">{add.data.added} tasks added. Existing tasks were preserved.</p>}
        {add.isError && <p role="alert">Tasks were not added. Please try again.</p>}
      </CardContent>
    </Card>
  );
}
